import {
  PrepErrorCodes,
  PrepJobDetailOption,
  PrepJobSourceType,
  WholesaleOrderOrigin,
} from "@deliverr/prep-service-client";
import { inboundClient, prepClient, replenishmentClient, wholesaleClient } from "Clients";
import { SPThunkAction } from "common/ReduxUtils";
import { getSellerId } from "common/user/UserSelectors";
import { toast } from "common/components/ui/Toast";
import {
  resetPrepDetails,
  resetPrepDetailsWithArchived,
  setPrepDetails,
  setPricingError,
  setSourceOrderDetails,
  setSourcePlanDetails,
} from "prep/store/PrepDetailActions";
import { logError } from "Logger";
import { ReplenishmentErrorCodes } from "common/constants/replenishment/ReplenishmentErrorCodes";
import { OrderResponse } from "@deliverr/replenishment-client";
import { prepWholesaleDetailsMapper } from "prep/util/prepWholesaleDetailsMapper";
import { loadWarehouses } from "common/deliverr/DeliverrActions";
import { setPrepJob } from "prep/store";

const fetchInboundPlan =
  (sourceId: number, sellerId: string) =>
  async (dispatch): Promise<void> => {
    const planDetails = await inboundClient.getShippingPlan(sellerId, sourceId);
    if (planDetails) {
      dispatch(setSourcePlanDetails(planDetails));
    }
  };

const fetchReplenishmentOrder =
  (wholesaleOrderId: string) =>
  async (dispatch): Promise<void> => {
    try {
      const orderResponse = await replenishmentClient.getOrder(wholesaleOrderId);
      if (orderResponse?.value) {
        dispatch(setSourceOrderDetails(orderResponse?.value));
      }
    } catch (err) {
      logError({ fn: "fetchReplenishmentOrder" }, err, "Error fetching wholesale order details.");
      const errData = err?.response?.data;
      if (errData?.error?.code === ReplenishmentErrorCodes.ORDER_NOT_FOUND) {
        dispatch(fetchWholesaleOrder(wholesaleOrderId));
      }
    }
  };

const fetchWholesaleOrder =
  (wholesaleOrderId: string) =>
  async (dispatch): Promise<void> => {
    const wholesaleOrderResponse = await wholesaleClient.getOrderAndShipments(wholesaleOrderId);
    if (wholesaleOrderResponse?.data) {
      const mappedResponse: OrderResponse = prepWholesaleDetailsMapper(wholesaleOrderResponse?.data);
      dispatch(setSourceOrderDetails(mappedResponse));
    }
  };

export const fetchPrepDetails =
  (prepId: string): SPThunkAction =>
  async (dispatch, getState): Promise<void> => {
    const state = getState();
    const sellerId = getSellerId(state);
    const ctx = { fn: "fetchPrepDetails" };

    try {
      const prepJobDetails = await prepClient.getJobByDpIdV2(prepId, [
        PrepJobDetailOption.COMPLETED_QUANTITIES,
        PrepJobDetailOption.WHOLESALE,
        PrepJobDetailOption.BILLING,
        PrepJobDetailOption.KITTING,
        PrepJobDetailOption.WHOLESALE,
        PrepJobDetailOption.FLOOR_LOADED_CONTAINER,
      ]);
      if (prepJobDetails) {
        dispatch(setPrepDetails(prepJobDetails));
      } else {
        throw new Error("Prep job details not found.");
      }
      const sourceId = prepJobDetails.sourceId;
      const sourceType = prepJobDetails.sourceType;
      const wholesaleOrderId = prepJobDetails.wholesaleDetails?.transferOrderId;

      if (prepJobDetails?.facilityId) {
        dispatch(loadWarehouses([prepJobDetails.facilityId]));
      }

      switch (sourceType) {
        case PrepJobSourceType.SHIPPING_PLAN: {
          await dispatch(fetchInboundPlan(Number(sourceId), sellerId));
          break;
        }
        case PrepJobSourceType.WHOLESALE_SHIPMENT:
          if (wholesaleOrderId) {
            if (prepJobDetails?.wholesaleOrderOrigin === WholesaleOrderOrigin.REPLENISHMENT) {
              await dispatch(fetchReplenishmentOrder(wholesaleOrderId));
            } else {
              await dispatch(fetchWholesaleOrder(wholesaleOrderId));
            }
          }
          break;
        default:
          break;
      }
    } catch (err) {
      const message = "Error fetching prep details. Please try again.";
      logError(ctx, err, message);
      const errData = err?.response?.data;
      if (errData?.code === PrepErrorCodes.PREP_JOB_ARCHIVED) {
        dispatch(resetPrepDetailsWithArchived());
      } else if (errData?.code === PrepErrorCodes.RATE_SHEET_ITEM_TYPE_NOT_FOUND) {
        dispatch(setPricingError(true));
      } else {
        dispatch(resetPrepDetails());
      }
      toast.error(message);
    }
  };

export const fetchPrepJob =
  (prepId: string): SPThunkAction =>
  async (dispatch): Promise<void> => {
    const ctx = { fn: "fetchPrepJob" };

    try {
      const prepJobResponse = await prepClient.getJobByDpIdV2(prepId);
      dispatch(setPrepJob(prepJobResponse));
    } catch (err) {
      const message = "Error fetching prep details. Please try again.";
      logError(ctx, err, message);
      toast.error(message);
    }
  };
